
import React, { useState, useCallback, useEffect } from 'react';
import { isSameDay, isWithinInterval, addDays, startOfWeek, endOfWeek, format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import CalendarDayContainer from './calendar/CalendarDayContainer';
import { CalendarEvent } from '../types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useShiftContextMenu } from '../hooks/useShiftContextMenu';
import CalendarRowView from './calendar/CalendarRowView';
import PerJobRowView from './calendar/PerJobRowView';
import { Clock } from 'lucide-react';
import { generateEventColor, sortEventsByTitle } from '../utils/colorUtils';
import { identifyGuardsWithMultipleShifts, hasGuardWithMultipleShifts } from '../utils/shiftUtils';
import { useTheme } from '@/contexts/ThemeContext';
import { useColorIntensity } from '../contexts/ColorIntensityContext';

interface CalendarStandardViewProps {
  daySchedules: { date: Date; events: CalendarEvent[] }[];
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => void;
  onDuplicateEvent: (event: CalendarEvent) => void;
  onDeleteEvent: (event: CalendarEvent) => void;
  onCopyShift: (event: CalendarEvent) => void;
  onCopyShiftWithoutGuards: (event: CalendarEvent) => void;
  onPasteShift: (targetDate: Date) => Promise<CalendarEvent | null>;
  onCopyDay?: (date: Date, events: CalendarEvent[], withGuards?: boolean) => void;
  onPasteDay?: (date: Date) => Promise<CalendarEvent[]>;
  onDeleteDay?: (date: Date, events: CalendarEvent[]) => void;
  onMarkForSwap?: (event: CalendarEvent) => void;
  onSwapWithMarked?: (event: CalendarEvent) => void;
  onCancelSwap?: () => void;
  markedShiftId?: string | null;
  permissions?: any;
  isCustomDateView?: boolean;
  isMonthView?: boolean;
  monthViewColumnWidth?: string;
  viewType?: 'standard' | 'per-job';
  guards?: any[];
  dateRange?: { start: Date; end: Date };
  isTotalWorkView?: boolean;
  calendarZoom?: number;
}

const CalendarStandardView: React.FC<CalendarStandardViewProps> = ({
  daySchedules,
  handleDayClick,
  handleEventClick,
  onDuplicateEvent,
  onDeleteEvent,
  onCopyShift,
  onCopyShiftWithoutGuards,
  onPasteShift,
  onCopyDay,
  onPasteDay,
  onDeleteDay,
  onMarkForSwap,
  onSwapWithMarked,
  onCancelSwap,
  markedShiftId,
  permissions = { 
    canCreate: false, 
    canUpdate: false, 
    canDelete: false, 
    canDrag: false 
  },
  isCustomDateView = false,
  isMonthView = false,
  monthViewColumnWidth,
  viewType = 'standard',
  guards = [],
  dateRange,
  isTotalWorkView = false,
  calendarZoom = 0.9
}) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { intensity } = useColorIntensity();
  
  // Generate title colors for all events
  const titleColors = React.useMemo(() => {
    const colors: Record<string, { backgroundColor: string; textColor: string }> = {};
    
    // Get all unique event titles
    const allEvents = daySchedules.flatMap(day => day.events);
    const uniqueTitles = [...new Set(allEvents.map(event => event.title))];
    
    // Generate color for each unique title
    uniqueTitles.forEach(title => {
      // Find the first event with this title to get a consistent color
      const firstEvent = allEvents.find(event => event.title === title);
      if (firstEvent) {
        colors[title] = generateEventColor(title, fromLocalISOString(firstEvent.start_date), theme, intensity);
      }
    });
    
    return colors;
  }, [daySchedules, theme, intensity]);
  
  // Identify guards with multiple shifts on the same day
  const guardsWithMultipleShifts = React.useMemo(() => {
    const allEvents = daySchedules.flatMap(day => day.events);
    return identifyGuardsWithMultipleShifts(allEvents);
  }, [daySchedules]);
  
  // Create a set of event IDs with multiple shifts for quick lookup
  const eventsWithMultipleShifts = React.useMemo(() => {
    const eventIds = new Set<string>();
    
    guardsWithMultipleShifts.forEach((eventIds2, guardId) => {
      eventIds2.forEach(eventId => {
        eventIds.add(eventId);
      });
    });
    
    return eventIds;
  }, [guardsWithMultipleShifts]);
  
  if (viewType === 'per-job') {
    return (
      <div className="w-full">
        <PerJobRowView
          daySchedules={daySchedules}
          guards={guards}
          handleDayClick={handleDayClick}
          handleEventClick={handleEventClick}
          onDuplicateEvent={onDuplicateEvent}
          onDeleteEvent={onDeleteEvent}
          onCopyShift={onCopyShift}
          onCopyShiftWithoutGuards={onCopyShiftWithoutGuards}
          onPasteShift={onPasteShift}
          onCopyDay={onCopyDay}
          onPasteDay={onPasteDay}
          onDeleteDay={onDeleteDay}
          permissions={permissions}
          titleColors={titleColors}
          isMonthView={isMonthView}
          showDuration={true}
          monthViewColumnWidth={monthViewColumnWidth}
          eventsWithMultipleShifts={eventsWithMultipleShifts}
          dateRange={dateRange}
          calendarZoom={calendarZoom}
        />
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <CalendarRowView
        daySchedules={daySchedules}
        handleDayClick={handleDayClick}
        handleEventClick={handleEventClick}
        onDuplicateEvent={onDuplicateEvent}
        onDeleteEvent={onDeleteEvent}
        onCopyShift={onCopyShift}
        onCopyShiftWithoutGuards={onCopyShiftWithoutGuards}
        onPasteShift={onPasteShift}
        onCopyDay={onCopyDay}
        onPasteDay={onPasteDay}
        onDeleteDay={onDeleteDay}
        onMarkForSwap={onMarkForSwap}
        onSwapWithMarked={onSwapWithMarked}
        onCancelSwap={onCancelSwap}
        markedShiftId={markedShiftId}
        permissions={permissions}
        titleColors={titleColors}
        isMonthView={isMonthView}
        showDuration={true}
        monthViewColumnWidth={monthViewColumnWidth}
        eventsWithMultipleShifts={eventsWithMultipleShifts}
        isTotalWorkView={isTotalWorkView}
        calendarZoom={calendarZoom}
      />
    </div>
  );
};

export default CalendarStandardView;
